import classNames from 'classnames'
import { EffectivePrivacy, User } from '@opallabs/concepts'
import { Spacing, ZIndex } from '@opallabs/gem'
import {
  Avatar,
  Button,
  Icon,
  Tooltip,
  type TooltipProps,
} from '@opallabs/lapidary'
import {
  PermissionSummaryContent,
  PermissionSummaryContentProps,
} from './PermissionSummaryContent'
import * as styles from './styles'

export type PermissionsSummaryProps = Omit<
  PermissionSummaryContentProps,
  'ownerId'
> &
  Pick<TooltipProps, 'onOpenChange' | 'legacyTheme'> & {
    avatarTooltipContent?: string
    className?: string
    hideAccessIcon?: boolean
    hideUserAvatar?: boolean
    onOpenShareModal?: () => void
    showOwnerName?: boolean
    tooltipZindex?: number
    resourceOwner?: User
  }

export const PermissionsSummary: React.FC<PermissionsSummaryProps> = ({
  avatarTooltipContent,
  className,
  effectivePrivacy: effectivePrivacyProp,
  hideAccessIcon,
  hideUserAvatar,
  legacyTheme,
  metadataQuery,
  onOpenChange,
  onOpenShareModal,
  resourceName,
  resourceOwner,
  showOwnerName = false,
  tooltipZindex,
}) => {
  const defaultOwnerText = `This ${resourceName} is owned by ${resourceOwner?.name ?? 'a deactivated user'}`
  const effectivePrivacy =
    effectivePrivacyProp ?? metadataQuery.data?.effectivePrivacy ?? null

  return (
    <div
      className={classNames('permissions-summary', className)}
      css={[styles.container, styles.containerDynamic(showOwnerName)]}
    >
      {!hideUserAvatar && (
        <Avatar
          css={styles.avatar}
          fullName={resourceOwner?.name ?? 'Inactive User'}
          size={20}
          src={resourceOwner?.asset?.url}
          tooltipped={Boolean(!showOwnerName || avatarTooltipContent)}
          customTooltipText={avatarTooltipContent ?? defaultOwnerText}
        />
      )}
      {showOwnerName && (
        <span css={styles.text}>
          Owned by {resourceOwner?.name ?? 'a deactivated user'}
        </span>
      )}
      {!hideAccessIcon && (
        <Tooltip
          side="top"
          content={
            <PermissionSummaryContent
              effectivePrivacy={effectivePrivacy}
              metadataQuery={metadataQuery}
              ownerId={resourceOwner?.uuid}
              resourceName={resourceName}
            />
          }
          sideOffset={Spacing.XS}
          zIndex={tooltipZindex ?? ZIndex.Drawer.Tooltip}
          onOpenChange={onOpenChange}
          legacyTheme={legacyTheme}
        >
          {onOpenShareModal ? (
            <Button
              buttonText="View Permissions"
              css={styles.tooltipButton}
              hideButtonText={true}
              icon={
                <Icon
                  glyph={`gem-${effectivePrivacy === EffectivePrivacy.EffectivelyPublic ? 'globe' : 'locked'}`}
                  ariaHidden
                />
              }
              name="Open share modal"
              onClick={(e) => {
                e.stopPropagation()
                onOpenShareModal?.()
              }}
              variant="ghost"
            />
          ) : (
            <Icon
              glyph={`gem-${effectivePrivacy === EffectivePrivacy.EffectivelyPublic ? 'globe' : 'locked'}`}
              ariaHidden
            />
          )}
        </Tooltip>
      )}
    </div>
  )
}
